import { Box, Flex, useDisclosure } from '@chakra-ui/react';
import { useState, useEffect } from 'react';
import Footer from '../components/Layout/footer';
import Sidebar from '../components/sidebar/Sidebar';
import Header from '../components/Header/Header';
import OnboardingModal from '../components/common/OnboardingModal';
import { BACKEND_API } from '../constants/backendApiEndpoints';

interface LayoutProps {
  children: React.ReactNode;
}

export const RegularLayout = ({ children }: LayoutProps) => {
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: true });
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingChecked, setOnboardingChecked] = useState(false);

  useEffect(() => {
    const checkOnboardingStatus = async () => {
      try {
        // Skip if user already dismissed onboarding this session
        if (sessionStorage.getItem('onboarding_skipped') === 'true') {
          setOnboardingChecked(true);
          return;
        }

        const token = localStorage.getItem('access_token');
        if (!token) {
          setOnboardingChecked(true);
          return;
        }

        const response = await fetch(BACKEND_API.USER.ONBOARDING_STATUS, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (!data.onboarding_completed) {
            setShowOnboarding(true);
          }
        }
      } catch (error) {
        console.error('Error checking onboarding status:', error);
      } finally {
        setOnboardingChecked(true);
      }
    };

    checkOnboardingStatus();
  }, []);

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
  };

  const handleOnboardingSkip = () => {
    sessionStorage.setItem('onboarding_skipped', 'true');
    setShowOnboarding(false);
  };

  return (
    <Flex h="100vh" w="100vw" bg="white" overflow="hidden">
      {/* Onboarding Modal */}
      <OnboardingModal 
        isOpen={showOnboarding} 
        onComplete={handleOnboardingComplete}
        onSkip={handleOnboardingSkip}
      />

      {/* Sidebar (Fixed position handled inside component) */}
      <Sidebar isOpen={isOpen} onToggle={onToggle} />

      {/* Main Column (Right of Sidebar) */}
      <Flex
        direction="column"
        flex="1"
        ml={isOpen ? '280px' : '0'}
        transition="margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
        h="100%"
        minW="0"
        overflow="hidden"
      >
        {/* Header */}
        <Header isSidebarOpen={isOpen} />

        {/* Scrollable Content Area */}
        {/* flex="1" + minH="0" is critical for nested flex scrolling */}
        <Box
          flex="1"
          overflowY="auto"
          minH="0"
          bg="white"
          p={0}
        >
          <Box p={6} maxW="1600px" mx="auto">
            {children}
          </Box>
        </Box>

        {/* Footer */}
        <Footer />
      </Flex>
    </Flex>
  );
};
